import type { ITestIdea } from '../types';

import { TEST_IDEA_PRIORITY, TEST_IDEA_TYPE } from './testIdeas';

// Copied from the design (Sample analytics for the empty analytics screen) — sample copy only.
export const DEMO_TEST_IDEAS: ITestIdea[] = [
  {
    id: 'sticky-add-to-cart',
    priority: TEST_IDEA_PRIORITY.HIGH,
    testType: TEST_IDEA_TYPE.TEMPLATE,
    claim: 'Mobile shoppers from ads rarely add to cart: the buy button scrolls out of view',
    keyMetricValue: '11.3%',
    keyMetricLabel: 'Add to cart rate',
    keyMetricComparison: 'for ad shoppers, vs 22.95% for this page',
    suggestion: 'Test a sticky Add to cart bar at the bottom of the mobile screen',
    detailTitle: 'Add a sticky Add to cart bar on mobile',
    problem:
      'Mobile shoppers who scroll down to read have to scroll all the way back up to find the buy button.',
    evidence: [
      [
        { text: 'Add to cart rate is ' },
        { text: '22.95%', isStrong: true },
        { text: ' on this page, but only ' },
        { text: '11.3%', isStrong: true },
        { text: ' for shoppers who land here from Facebook/Instagram ads, vs ' },
        { text: '42.1%', isStrong: true },
        { text: ' for shoppers coming from another page' },
      ],
      [
        { text: '90.7%', isStrong: true },
        { text: ' of sessions are on mobile, rising to ' },
        { text: '97.1%', isStrong: true },
        { text: ' for ad shoppers' },
      ],
      [
        { text: 'Buy button ' },
        { text: '1.6 screens', isStrong: true },
        { text: ' down on a page ' },
        { text: '~18 screens', isStrong: true },
        { text: ' long, with no sticky buy button' },
      ],
    ],
    hypothesis:
      'A buy button that is always visible at the bottom of the screen saves shoppers who have decided to buy from scrolling back up, so the mobile add-to-cart rate will go up.',
    expectedResult:
      'If the buy button is always in view on mobile, then more ad shoppers will add to cart, because they no longer have to scroll back up.',
  },
  {
    id: 'reviews-under-buy-button',
    priority: TEST_IDEA_PRIORITY.MEDIUM,
    testType: TEST_IDEA_TYPE.TEMPLATE,
    claim: 'Half of ad shoppers click nothing, while reviews sit far from the buy button',
    keyMetricValue: '54.1%',
    keyMetricLabel: 'Click-through rate',
    keyMetricComparison: 'for ad shoppers, vs 66.03% for this page',
    suggestion: 'Test 2–3 short reviews right below the Add to cart button',
    detailTitle: 'Show 2–3 reviews right under the buy button',
    problem:
      'Shoppers from ads decide at the buy button, but the reviews sit six screens further down.',
    evidence: [
      [
        { text: 'Click-through rate is ' },
        { text: '66.03%', isStrong: true },
        { text: ' on this page, but only ' },
        { text: '54.1%', isStrong: true },
        { text: ' for ad shoppers, vs ' },
        { text: '81.5%', isStrong: true },
        { text: ' for shoppers coming from another page' },
      ],
      [
        { text: '4,126', isStrong: true },
        { text: ' of ' },
        { text: '7,082', isStrong: true },
        { text: ' sessions come from Facebook/Instagram, and ' },
        { text: '3,388', isStrong: true },
        { text: ' of them land straight on this page' },
      ],
      [
        { text: '2,416', isStrong: true },
        { text: ' reviews at ' },
        { text: '4.9★', isStrong: true },
        { text: ' sit ' },
        { text: '~6 screens', isStrong: true },
        { text: ' below the buy button, while next to it there is only a "2416+ Customers" line' },
      ],
    ],
    hypothesis:
      'New shoppers from ads need proof at the moment they decide. Reviews placed next to the buy button will build trust and raise the add-to-cart rate.',
    expectedResult:
      'If shoppers see real reviews where they decide, then more ad shoppers will add to cart, because the proof is there at that moment.',
  },
  {
    id: 'raise-price-ten-percent',
    priority: TEST_IDEA_PRIORITY.MEDIUM,
    testType: TEST_IDEA_TYPE.PRICE,
    claim: 'Shoppers buy right away at full price: there is room to raise it',
    keyMetricValue: '88%',
    keyMetricLabel: 'Full-price sales',
    keyMetricComparison: 'of jars sold with no discount',
    suggestion: 'Test a 10% price increase: $25 → $27.50',
    detailTitle: 'Raise the Formula 49 price 10% for new customers',
    problem:
      'Shoppers pay full price and buy fast, so $25 sits below what they are willing to pay.',
    evidence: [
      [
        { text: 'Conversion rate is ' },
        { text: '13.99%', isStrong: true },
        { text: '. At +10% price it only needs to stay above ' },
        { text: '12.72%', isStrong: true },
        { text: ' to keep Revenue per visitor at ' },
        { text: '$7.51', isStrong: true },
      ],
      [
        { text: '88%', isStrong: true },
        { text: ' of jars sold at full list price, ' },
        { text: '12%', isStrong: true },
        { text: ' at the subscription price (−15%), none in a sale' },
      ],
      [
        { text: '82%', isStrong: true },
        { text: ' of shoppers buy within ' },
        { text: '0–1 days', isStrong: true },
        { text: ' of their first visit' },
      ],
      [
        { text: '$25', isStrong: true },
        { text: ' for 2oz, vs ' },
        { text: '~$23.50', isStrong: true },
        { text: ' middle competitor price ($20–26)' },
      ],
    ],
    hypothesis:
      'A 10% increase will lower the purchase rate by less than the break-even point, so revenue per visitor goes up. Product cost stays the same, so if RPV wins, profit wins too.',
    expectedResult:
      'If Formula 49 costs 10% more, then revenue per visitor will go up, because conversion only has to stay above 12.72% (now 13.99%) to break even.',
  },
];
